import {
  ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostBinding, Input, OnInit, Output, ViewChild,
} from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { LangChangeEvent } from '@ngx-translate/core/lib/translate.service';
import {
  debounceTime, distinctUntilChanged, tap, Observable, Subject, takeUntil, throttleTime
} from 'rxjs';

import { CustomIcons } from '@shared/models/custom-icons';
import { IDestroy, TakeUntilDestroy } from '@shared/utils/auto-unsubscribe';
import { SnackBarService } from '@shared/components/snackbar-message/services/snackbar.service';
import { MirSnackbarType } from '@shared/components/snackbar-message/model/snackbar';
import { Locale } from '@shared/models/locale';

export type StartSearch = {
  searchString: string;
  isClickStart: boolean;
};

@Component({
  selector: 'app-search-input',
  templateUrl: './search-input.component.html',
  styleUrls: ['./search-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@TakeUntilDestroy
export class SearchInputComponent implements OnInit, IDestroy {
  private _searchString = '';
  public error = false;
  public isRuLocale = true;
  public langChange$: Observable<LangChangeEvent>;
  @Output() public readonly searchTextChange = new EventEmitter<string>();
  @Output() public readonly startSearch = new EventEmitter<StartSearch>();
  @Output() public readonly clear = new EventEmitter<void>();
  @Output() public readonly inputClickEvent = new EventEmitter();
  public get searchString(): string {
    return this._searchString;
  }
  @Input() public set searchString(value: string) {
    if (value !== this.searchInputControl.value || value === '') {
      this._searchString = value;
      this.searchInputControl.setValue(this._searchString);
      this.checkMinError(value);
    }
  }
  @ViewChild('searchInput') public searchInput: ElementRef;
  @Input() public disabledInput = false;
  @Input() public minValue = 0;
  @Input()
  @HostBinding('class.wo-global-search')
  public hideGlobalSearchLink = false;

  public searchInputControl = new UntypedFormControl();

  public searchDebouncer$ = this.searchInputControl.valueChanges.pipe(
    distinctUntilChanged(),
    debounceTime(100),
    tap((val: string) => {
      if (val.trim().length === 0) {
        this.searchInputControl.patchValue('', { emitEvent: false });
      } else {
        this.checkMinError(val);
      }
      this._searchString = val.trim();
      this.searchTextChange.emit(this._searchString);
    })
  );

  public icons = CustomIcons;

  public isMobile = this.breakpointObserver.isMatched(['(max-width: 400px)']);

  private delayedEnterEvent$ = new Subject<string>();

  public componentDestroy: () => Subject<any>;

  constructor(
    private route: Router,
    private translateService: TranslateService,
    private breakpointObserver: BreakpointObserver,
    private snackBar: SnackBarService
  ) {}

  private setText(lang: Locale): void {
    if (lang === Locale.RU_RU) {
      this.isRuLocale = true;
    } else {
      this.isRuLocale = false;
    }
  }

  private checkMinError(val: string): void {
    this.error = this.minValue !== 0 && val?.length !== 0 && val?.length < this.minValue;
  }

  public ngOnInit(): void {
    this.setText(this.translateService.currentLang as Locale);
    this.langChange$ = this.translateService.onLangChange.pipe(
      tap(res => {
        this.setText(res.lang as Locale);
      })
    );
    this.delayedEnterEvent$
      .pipe(
        throttleTime(1000),
        takeUntil(this.componentDestroy())
      )
      .subscribe((text) => this.searchStart(text));
  }

  delayOnEnter(searchString: string) {
    this.delayedEnterEvent$.next(searchString);
  }

  delayOnSearch(searchString: string) {
    this.delayedEnterEvent$.next(searchString);
  }

  public goGlobalSearch(): void {
    this.route.navigate([`globalsearch/${this.searchString}`]);
  }

  public onSearchPress(): void {
    // empty
  }

  public clearValue(): void {
    this.searchInputControl.reset();
  }

  public onClear(): void {
    this._searchString = '';
    this.error = false;
    this.searchInputControl.reset('');
    this.startSearch.emit({
      isClickStart: true,
      searchString: this._searchString
    });
    this.clear.emit();
  }

  public searchStart(searchString: string, isClickStart = true): void {
    if (!searchString || searchString.length === 0) {
      this.searchInput.nativeElement.focus();
    }

    if (this.minValue !== 0
      && (!searchString || searchString.length < this.minValue)
      && (searchString && searchString.length !== 0)
    ) {
      this.snackBar.open(MirSnackbarType.Warning, [
        {
          message: 'SNACK_BAR_MESSAGE.SEARCH_MIN_VALUE',
          translate: true
        }
      ]);
    } else {
      this.startSearch.emit({
        isClickStart: isClickStart,
        searchString: searchString
      });
    }
  }

  public inputClick(): void {
    this.inputClickEvent.emit();
  }
}
